import { type EntryPoint, type EntryPointComponent, type PreloadFetchPolicy } from 'react-relay';

import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import parameters, {
	type UserProfileCardEntryPointQuery,
	type UserProfileCardEntryPointQuery$variables,
} from './__generated__/UserProfileCardEntryPointQuery.graphql';

const fetchPolicy: PreloadFetchPolicy = 'store-or-network';

/**
 * Inputs the trigger and entry-point need to fetch a user's profile card.
 */
export type UserProfileCardRelayTriggerParams = {
	/** Account ID of the user whose card is being shown. */
	accountId: string;
	/** Site ID of the tenant the viewer is currently in. */
	cloudId: string;
	/** Organisation ID. Required for knowledge discovery query. */
	orgId: string;
	/** Account ID of the user viewing the card. Required for knowledge discovery query. */
	requestingUserId: string;
};

const entryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryRef: UserProfileCardEntryPointQuery;
		},
		Record<string, never>,
		{
			cloudId: string;
			orgId: string;
			requestingUserId: string;
			testId?: string;
		},
		Record<string, never>
	>,
	UserProfileCardRelayTriggerParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_user-profile-card-entrypoint" */ './UserProfileCardEntryPoint'
			),
		{
			moduleId: 'user-profile-card-entrypoint',
		},
	),
	getPreloadProps: ({ accountId, cloudId, orgId, requestingUserId }: UserProfileCardRelayTriggerParams) => {
		const variables: UserProfileCardEntryPointQuery$variables = {
			accountId,
		};
		return {
			queries: {
				queryRef: {
					parameters,
					variables,
					options: {
						fetchPolicy,
					},
				},
			},
		};
	},
});

export { entryPoint };
